import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
    Button,
    Input,
    ConfirmDialog,
    Dialog,
    DialogHeader,
    DialogTitle,
    DialogContent,
    DialogFooter,
    gtagEvent,
    formatPhone,
    formatCpfCnpj,
    stripCountryCode,
    onlyDigits,
    vars,
    Badge,
    type BadgeTone,
} from '@agendor/ui';
import { Modal } from '../components/Modal';
import { FormGroup, InputGroup } from '../components/Input';
import { RefreshButton } from '../components/RefreshButton';
import BusinessService, {
    BusinessDetails,
    Client,
    ClientAlreadyExistsError,
    ClientSubscriptionStatus,
} from '../business-service';
import { useSectionVisibility } from './section-visibility-context';
import { useListModalRoute } from '../navigation';
import { useToast } from '../toast-context';
import getClientContactInfo from '../get-client-contact-info';
import { isIOS, isStandalone } from '../platform';
import { formatCurrency } from '../pricing';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 800px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Title = styled.h1`
    font-size: 1.5rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
`;

const ClientList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ClientCard = styled.div<{ $clickable?: boolean; $highlighted: boolean }>`
    background-color: ${(p) => (p.$highlighted ? vars.primaryColorLight : '#fff')};
    border: 1px solid ${(p) => (p.$highlighted ? vars.accentColor : vars.dividerColor)};
    transition:
        background-color 0.6s,
        border-color 0.6s;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    cursor: ${(p) => (p.$clickable !== false ? 'pointer' : 'default')};

    ${(p) =>
        p.$clickable !== false &&
        `
        &:hover {
            border-color: ${vars.foregroundColor};
            background-color: ${vars.backgroundColorBright};
        }
    `}

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const ClientInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ClientName = styled.h3<{ muted?: boolean }>`
    margin: 0;
    font-size: 1.1rem;
    color: ${(p) => (p.muted ? '#888' : vars.foregroundColor)};
    font-weight: ${(p) => (p.muted ? '400' : '600')};
    font-style: ${(p) => (p.muted ? 'italic' : 'normal')};
`;

const ClientDetails = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;

    @media (max-width: 600px) {
        width: 100%;
        justify-content: space-between;
    }
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;

    @media (max-width: 600px) {
        align-items: flex-start;
    }
`;

const DetailLabel = styled.span`
    font-size: 0.8rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
`;

const DetailValue = styled.span`
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const FilterChip = styled.button<{ $active: boolean }>`
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid ${(p) => (p.$active ? vars.foregroundColor : vars.dividerColor)};
    background-color: ${(p) => (p.$active ? vars.primaryColorLight : 'transparent')};
    color: ${vars.foregroundColor};
    font: inherit;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover {
        border-color: ${vars.foregroundColor};
    }

    @media (max-width: 600px) {
        width: 36px;
        height: 36px;
        padding: 0;
        justify-content: center;
        border-radius: 8px;
        border-color: ${(p) => (p.$active ? vars.foregroundColor : vars.dividerColor)};
    }
`;

const FilterChipLabel = styled.span`
    @media (max-width: 600px) {
        display: none;
    }
`;

const FilterIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73z" />
    </svg>
);

const SubscriptionBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
`;

const SubscriptionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SubscriptionTitle = styled.strong`
    font-size: 1rem;
    color: ${vars.foregroundColor};
`;

const SubscriptionDetail = styled.div`
    font-size: 0.9rem;
    color: #666;
`;

const HIGHLIGHT_DURATION_MS = 4000;

const statusLabels: Record<ClientSubscriptionStatus, string> = {
    active: 'Ativa',
    pending_payment: 'Aguardando',
    overdue: 'Inadimplente',
    cancelled: 'Cancelada',
};

const statusTones: Record<ClientSubscriptionStatus, BadgeTone> = {
    active: 'success',
    pending_payment: 'warning',
    overdue: 'danger',
    cancelled: 'muted',
};

const EMPTY_SEGMENTS: string[] = [];

export default function ClientsSection({
    service,
    business,
    clients,
    isLoadingClients,
    clientsLoadError,
    ensureClientsLoaded,
    onClientsChange,
    isActive,
    modalSegments,
    onNavigate,
    isOwner,
}: {
    service: BusinessService;
    business: BusinessDetails | null;
    clients: Client[];
    isLoadingClients: boolean;
    clientsLoadError: string | null;
    ensureClientsLoaded: () => void;
    onClientsChange: () => Promise<void>;
    isActive: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    isOwner: boolean;
}) {
    const subscriptionsEnabled = business?.clientSubscriptionsEnabled ?? false;
    const isVisible = useSectionVisibility();
    const { showToast } = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState<Partial<Client>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [filterSubscribers, setFilterSubscribers] = useState(false);
    const [existingClient, setExistingClient] = useState<ClientAlreadyExistsError['existingClient'] | null>(null);
    const [highlightedClientId, setHighlightedClientId] = useState<number | null>(null);

    // Subscription create form
    const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
    const [draftPriceReais, setDraftPriceReais] = useState('');
    const [draftBillingDay, setDraftBillingDay] = useState('');
    const [draftCpfCnpj, setDraftCpfCnpj] = useState('');
    const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);

    // Subscription cancel
    const [cancelTarget, setCancelTarget] = useState<Client | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);

    // Refresh button (iOS PWA only)
    const showRefreshButton = useMemo(() => isIOS() && isStandalone(), []);

    // Ensure clients are loaded when section becomes visible
    useEffect(() => {
        if (!isVisible) return;
        ensureClientsLoaded();
    }, [isVisible, ensureClientsLoaded]);

    const filteredClients = useMemo(() => {
        if (!filterSubscribers) return clients;
        return clients.filter((c) => c.activeSubscription !== null);
    }, [clients, filterSubscribers]);

    const findById = useCallback((id: string) => clients.find((c) => String(c.id) === id), [clients]);

    const {
        state: modalState,
        error: modalRouteError,
        openNew,
        openItem,
        close,
    } = useListModalRoute<Client>({
        isActive,
        ready: !isLoadingClients,
        segments: modalSegments,
        onNavigate,
        findById,
        label: 'clientes',
        baseSegments: EMPTY_SEGMENTS,
    });

    useEffect(() => {
        if (highlightedClientId === null) return;
        document
            .querySelector(`[data-testid="client-item-${highlightedClientId}"]`)
            ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const timeout = setTimeout(() => setHighlightedClientId(null), HIGHLIGHT_DURATION_MS);
        return () => clearTimeout(timeout);
    }, [highlightedClientId]);

    useEffect(() => {
        setExistingClient(null);
        if (modalState.kind === 'none') {
            setIsModalOpen(false);
            setEditingClient({});
            setShowSubscriptionForm(false);

            return;
        }
        if (modalState.kind === 'new') {
            setEditingClient({});
            setShowSubscriptionForm(false);

            setIsModalOpen(true);
            return;
        }
        // Strip country code (55) from phone for display
        setEditingClient({
            ...modalState.item,
            phoneNumber: modalState.item.phoneNumber
                ? formatPhone(stripCountryCode(modalState.item.phoneNumber))
                : null,
        });
        setShowSubscriptionForm(false);
        setIsModalOpen(true);
    }, [modalState]);

    const handleAddClick = () => {
        gtagEvent('clients_new_click');
        openNew();
    };

    const handleEditClick = (client: Client) => {
        if (!isOwner) return;
        gtagEvent('clients_item_click');
        openItem(String(client.id));
    };

    const handleCloseModal = () => {
        gtagEvent('clients_cancel_click');
        close();
    };

    const handleSave = async () => {
        gtagEvent('clients_save_click');
        if (!editingClient.name) {
            showToast('Nome é obrigatório.', 'error', 'modal');
            return;
        }

        setIsSaving(true);
        try {
            if (modalState.kind === 'new') {
                if (!editingClient.phoneNumber) {
                    showToast('WhatsApp é obrigatório.', 'error', 'modal');
                    setIsSaving(false);
                    return;
                }

                const rawPhone = onlyDigits(editingClient.phoneNumber);
                if (rawPhone.length < 10 || rawPhone.length > 11) {
                    showToast('Número de WhatsApp inválido.', 'error', 'modal');
                    setIsSaving(false);
                    return;
                }

                // Add country code 55 when sending to backend
                await service.createClient({
                    name: editingClient.name,
                    phoneNumber: '55' + rawPhone,
                });
                showToast('Cliente criado com sucesso!', 'success', 'app');
            } else if (modalState.kind === 'item') {
                // Updating existing client (name only)
                await service.setClientName({
                    clientId: Number(modalState.id),
                    name: editingClient.name,
                });
                showToast('Cliente atualizado com sucesso!', 'success', 'app');
            }
            await onClientsChange();
            handleCloseModal();
        } catch (error) {
            if (error instanceof ClientAlreadyExistsError) {
                gtagEvent('clients_already_exists');
                setExistingClient(error.existingClient);
                // The client may have been created after the list loaded; refresh so it can be opened.
                await onClientsChange();
                return;
            }
            console.error('Failed to save client', error);
            showToast('Não foi possível salvar o cliente.', 'error', 'modal');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveNameOnExistingClient = async () => {
        if (!existingClient || !editingClient.name) return;
        gtagEvent('clients_save_name_on_existing_click');
        setIsSaving(true);
        try {
            await service.setClientName({ clientId: existingClient.id, name: editingClient.name });
            showToast('Nome salvo no cadastro existente.', 'success', 'app');
            await onClientsChange();
            setHighlightedClientId(existingClient.id);
            close();
        } catch (error) {
            console.error('Failed to save name on existing client', error);
            showToast('Não foi possível salvar o cliente.', 'error', 'modal');
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenExistingClient = () => {
        if (!existingClient) return;
        gtagEvent('clients_open_existing_click');
        openItem(String(existingClient.id));
    };

    const handleInputChange = (field: keyof Client, value: string) => {
        if (field === 'phoneNumber') {
            setEditingClient((prev) => ({ ...prev, [field]: formatPhone(value) }));
        } else {
            setEditingClient((prev) => ({ ...prev, [field]: value }));
        }
    };

    const handleCreateSubscription = async (e: React.FormEvent) => {
        e.preventDefault();
        if (modalState.kind !== 'item') return;

        const priceInCents = Math.round(parseFloat(draftPriceReais.replace(',', '.')) * 100);
        if (isNaN(priceInCents) || priceInCents < 500) {
            showToast('Valor mínimo: R$ 5,00.', 'error', 'modal');
            return;
        }
        const billingDay = parseInt(draftBillingDay);
        if (isNaN(billingDay) || billingDay < 1 || billingDay > 28) {
            showToast('Dia de cobrança deve ser entre 1 e 28.', 'error', 'modal');
            return;
        }

        setIsCreatingSubscription(true);
        try {
            await service.createClientSubscription({
                clientId: Number(modalState.id),
                priceInCents,
                billingDay,
                cpfCnpj: draftCpfCnpj || undefined,
            });
            gtagEvent('client_subscription_created');
            setShowSubscriptionForm(false);
            showToast('Assinatura criada! O link de pagamento foi enviado ao cliente.', 'success', 'modal');
            await onClientsChange();
        } catch (error) {
            console.error(error);
            showToast('Erro ao criar assinatura.', 'error', 'modal');
        } finally {
            setIsCreatingSubscription(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!cancelTarget?.activeSubscription) return;
        setIsCancelling(true);
        try {
            await service.cancelClientSubscription(cancelTarget.id);
            gtagEvent('client_subscription_cancelled');
            showToast('Assinatura cancelada.', 'success', 'modal');
            setCancelTarget(null);
            await onClientsChange();
        } catch {
            showToast('Erro ao cancelar assinatura.', 'error', 'modal');
        } finally {
            setIsCancelling(false);
        }
    };

    const openSubscriptionForm = () => {
        setDraftPriceReais('');
        setDraftBillingDay('');
        setDraftCpfCnpj('');
        setShowSubscriptionForm(true);
    };

    const currentClient = modalState.kind === 'item' ? modalState.item : null;
    const currentSubscription = currentClient?.activeSubscription ?? null;

    return (
        <Container>
            <Header>
                <Title>Clientes</Title>
                {modalRouteError && (
                    <div
                        style={{
                            padding: '8px 10px',
                            borderRadius: 6,
                            border: `1px solid ${vars.dangerColorDark}`,
                            background: vars.dangerTintSoft,
                            color: vars.foregroundColor,
                        }}
                    >
                        {modalRouteError}
                    </div>
                )}
                <HeaderActions>
                    {subscriptionsEnabled && (
                        <FilterChip
                            $active={filterSubscribers}
                            onClick={() => setFilterSubscribers((v) => !v)}
                            data-testid="filter-subscribers"
                        >
                            <FilterIcon />
                            <FilterChipLabel>Assinantes</FilterChipLabel>
                        </FilterChip>
                    )}
                    {showRefreshButton && (
                        <RefreshButton
                            onRefresh={() => {
                                gtagEvent('clients_refresh_click');
                                onClientsChange();
                            }}
                            disabled={isLoadingClients}
                        />
                    )}
                    <Button primary data-testid="new-client-btn" onClick={handleAddClick}>
                        Novo Cliente
                    </Button>
                </HeaderActions>
            </Header>

            {isLoadingClients ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Carregando clientes...</div>
            ) : clientsLoadError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{clientsLoadError}</div>
                    <Button onClick={onClientsChange}>Tentar novamente</Button>
                </div>
            ) : (
                <ClientList>
                    {filteredClients.length > 0 ? (
                        filteredClients.map((client) => {
                            const contactInfo = getClientContactInfo(client);
                            return (
                                <ClientCard
                                    key={client.id}
                                    data-testid={`client-item-${client.id}`}
                                    $clickable={isOwner}
                                    $highlighted={client.id === highlightedClientId}
                                    data-highlighted={client.id === highlightedClientId}
                                    onClick={() => handleEditClick(client)}
                                >
                                    <ClientInfo>
                                        <ClientName muted={!client.name}>{client.name || 'Sem nome'}</ClientName>
                                        {subscriptionsEnabled && client.activeSubscription && (
                                            <div
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                                data-testid="subscription-status"
                                            >
                                                <Badge tone={statusTones[client.activeSubscription.status]}>
                                                    {statusLabels[client.activeSubscription.status]}
                                                </Badge>
                                                <SubscriptionDetail>
                                                    {formatCurrency(client.activeSubscription.priceInCents)}/mês
                                                </SubscriptionDetail>
                                            </div>
                                        )}
                                    </ClientInfo>
                                    <ClientDetails>
                                        <DetailItem>
                                            <DetailLabel>{contactInfo.label}</DetailLabel>
                                            <DetailValue>{contactInfo.value}</DetailValue>
                                        </DetailItem>
                                    </ClientDetails>
                                </ClientCard>
                            );
                        })
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            {filterSubscribers ? 'Nenhum cliente assinante.' : 'Nenhum cliente cadastrado.'}
                        </div>
                    )}
                </ClientList>
            )}

            {/* Edit/New Client Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={modalState.kind === 'new' ? 'Novo Cliente' : 'Editar Cliente'}
                data-testid="client-modal"
                footer={
                    showSubscriptionForm ? (
                        <>
                            <Button onClick={() => setShowSubscriptionForm(false)} disabled={isCreatingSubscription}>
                                Voltar
                            </Button>
                            <Button primary type="submit" form="subscription-form" disabled={isCreatingSubscription}>
                                {isCreatingSubscription ? 'Criando...' : 'Criar assinatura'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button data-testid="modal-cancel" onClick={handleCloseModal} disabled={isSaving}>
                                Cancelar
                            </Button>
                            <Button
                                data-testid="modal-save"
                                primary
                                type="submit"
                                form="client-form"
                                disabled={isSaving}
                            >
                                {isSaving ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </>
                    )
                }
            >
                {showSubscriptionForm ? (
                    <form id="subscription-form" onSubmit={handleCreateSubscription}>
                        <FormGroup>
                            <InputGroup>
                                Valor mensal (R$)
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="100,00"
                                    value={draftPriceReais}
                                    onChange={(e) => setDraftPriceReais(e.target.value)}
                                />
                            </InputGroup>
                            <InputGroup>
                                Dia de cobrança (1-28)
                                <Input
                                    type="number"
                                    min={1}
                                    max={28}
                                    placeholder="15"
                                    value={draftBillingDay}
                                    onChange={(e) => setDraftBillingDay(e.target.value)}
                                />
                            </InputGroup>
                            {currentClient?.requiresCpf && (
                                <InputGroup>
                                    CPF/CNPJ do cliente
                                    <Input
                                        type="text"
                                        placeholder="000.000.000-00"
                                        value={formatCpfCnpj(draftCpfCnpj)}
                                        onChange={(e) => setDraftCpfCnpj(onlyDigits(e.target.value).slice(0, 14))}
                                    />
                                </InputGroup>
                            )}
                        </FormGroup>
                    </form>
                ) : (
                    <form
                        id="client-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSave();
                        }}
                    >
                        <FormGroup>
                            <InputGroup>
                                Nome
                                <Input
                                    type="text"
                                    value={editingClient.name || ''}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="Ex.: João Silva"
                                    disabled={isSaving}
                                />
                            </InputGroup>
                            {modalState.kind === 'new' ? (
                                <InputGroup>
                                    WhatsApp
                                    <Input
                                        type="tel"
                                        value={editingClient.phoneNumber || ''}
                                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                        placeholder="(00) 00000-0000"
                                        maxLength={15}
                                        disabled={isSaving}
                                    />
                                </InputGroup>
                            ) : (
                                // Editing: show all available contact info
                                <>
                                    {editingClient.phoneNumber && (
                                        <InputGroup>
                                            WhatsApp
                                            <Input type="tel" value={editingClient.phoneNumber} readOnly disabled />
                                        </InputGroup>
                                    )}
                                    {editingClient.email && (
                                        <InputGroup>
                                            E-mail
                                            <Input type="email" value={editingClient.email} readOnly disabled />
                                        </InputGroup>
                                    )}
                                </>
                            )}

                            {/* Subscription info in edit modal */}
                            {subscriptionsEnabled &&
                                modalState.kind === 'item' &&
                                (currentSubscription ? (
                                    <SubscriptionBox>
                                        <SubscriptionHeader>
                                            <SubscriptionTitle>Assinatura</SubscriptionTitle>
                                            <Badge tone={statusTones[currentSubscription.status]}>
                                                {statusLabels[currentSubscription.status]}
                                            </Badge>
                                        </SubscriptionHeader>
                                        <SubscriptionDetail>
                                            {formatCurrency(currentSubscription.priceInCents)}/mês — dia{' '}
                                            {currentSubscription.billingDay}
                                        </SubscriptionDetail>
                                        {currentSubscription.creditCard && (
                                            <SubscriptionDetail>
                                                {currentSubscription.creditCard.brand} final{' '}
                                                {currentSubscription.creditCard.last4}
                                            </SubscriptionDetail>
                                        )}
                                        <Button
                                            type="button"
                                            danger
                                            onClick={() => setCancelTarget(currentClient)}
                                            style={{ alignSelf: 'flex-start', marginTop: '4px' }}
                                            data-testid="cancel-subscription-btn"
                                        >
                                            Cancelar assinatura
                                        </Button>
                                    </SubscriptionBox>
                                ) : (
                                    <Button
                                        type="button"
                                        onClick={openSubscriptionForm}
                                        style={{ alignSelf: 'flex-start' }}
                                        data-testid="create-subscription-btn"
                                    >
                                        Criar assinatura
                                    </Button>
                                ))}
                        </FormGroup>
                    </form>
                )}
            </Modal>

            <Dialog
                isOpen={existingClient !== null}
                onClose={() => setExistingClient(null)}
                allowCloseOnOverlay={!isSaving}
                data-testid="client-already-exists"
            >
                <DialogHeader>
                    <DialogTitle>Cliente já cadastrado</DialogTitle>
                </DialogHeader>
                <DialogContent>
                    Este número já está na sua lista de clientes
                    {existingClient?.name ? (
                        <>
                            {' '}
                            como <strong>{existingClient.name}</strong>.
                        </>
                    ) : (
                        <>
                            , ainda sem nome.
                            {isOwner && (
                                <>
                                    {' '}
                                    Quer salvar o nome <strong>{editingClient.name}</strong> nesse cadastro?
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogFooter>
                    <Button
                        type="button"
                        data-testid="client-already-exists-back"
                        onClick={() => setExistingClient(null)}
                        disabled={isSaving}
                    >
                        Voltar
                    </Button>
                    {isOwner &&
                        (existingClient?.name ? (
                            <Button
                                type="button"
                                primary
                                data-testid="open-existing-client"
                                onClick={handleOpenExistingClient}
                            >
                                Abrir cadastro
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                primary
                                data-testid="save-name-on-existing-client"
                                onClick={handleSaveNameOnExistingClient}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Salvando...' : 'Salvar nome'}
                            </Button>
                        ))}
                </DialogFooter>
            </Dialog>

            {/* Cancel Subscription Confirmation */}
            <ConfirmDialog
                isOpen={cancelTarget !== null}
                onCancel={() => setCancelTarget(null)}
                onConfirm={handleCancelSubscription}
                title="Cancelar Assinatura"
                message="Tem certeza? Agendamentos vinculados serão cancelados."
                confirmText="Cancelar"
                loadingText="Cancelando..."
                isLoading={isCancelling}
            />
        </Container>
    );
}
